import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NotificationHeader } from '@app/core/Enum/notification';
import { GlobalComponent, SideListModel } from '@app/core/Models';
import { NotificationService } from '@app/core/Services';
import {
  GetAutoRoundingSettingById,
  SaveAutoRoundingSetting,
  UpdateAutoRoundingSetting,
} from '@app/core/Store';
import { Store } from '@ngxs/store';

export interface AddRoundingFigureDialogData {
  profitLossAccounts: any[];
  balanceSheetAccounts: any[];
  fundList: SideListModel[];
  id?: string;
}

interface AccountingPeriod {
  id: string;
  name: string;
  fromDate: string;
  toDate: string;
  companyId: string;
  isLocked: boolean;
  isCeased: boolean;
}

@Component({
  selector: 'app-add-rounding-figure',
  templateUrl: './add-rounding-figure.component.html',
  styleUrls: ['./add-rounding-figure.component.scss'],
})
export class AddRoundingFigureComponent implements OnInit {
  addRoundingForm: FormGroup;
  isEditMode = false;
  accountingPeriods: AccountingPeriod[] = [];
  private readonly accountingPeriodDateFallback = Number.MAX_SAFE_INTEGER;

  get profitLossAccounts() {
    return this.data.profitLossAccounts;
  }
  get balanceSheetAccounts() {
    return this.data.balanceSheetAccounts;
  }
  get fundList() {
    return this.data.fundList;
  }

  constructor(
    private store: Store,
    private notifier: NotificationService,
    private dialogRef: MatDialogRef<AddRoundingFigureComponent>,
    private globalComponent: GlobalComponent,
    @Inject(MAT_DIALOG_DATA) public data: AddRoundingFigureDialogData
  ) {}

  ngOnInit(): void {
    this.isEditMode = !!this.data.id;
    this.loadAccountingPeriods();

    this.addRoundingForm = new FormGroup({
      accountingPeriodId: new FormControl(null, [Validators.required]),
      profitLossAccountId: new FormControl(null, [Validators.required]),
      balanceSheetAccountId: new FormControl(null, [Validators.required]),
      fundId: new FormControl(null, [Validators.required]),
    });

    if (this.isEditMode) {
      this.store
        .dispatch(new GetAutoRoundingSettingById(this.data.id!))
        .subscribe((res) => {
          const detail = res.accountProduction?.autoRoundingSettingDetail;
          if (detail) {
            const matchedFund = this.findFundByIdOrName(
              detail.fundRoundingAccountId,
              detail.fundRoundingAccountName
            );

            this.addRoundingForm.patchValue({
              accountingPeriodId: detail.accountingPeriodId,
              profitLossAccountId: detail.profitLossRoundingAccountId,
              balanceSheetAccountId: detail.balanceSheetRoundingAccountId,
              fundId: matchedFund?.id || detail.fundRoundingAccountId || null,
            });
          }
        });
    }
  }

  onSave(): void {
    if (this.addRoundingForm.invalid) {
      this.addRoundingForm.markAllAsTouched();
      return;
    }

    const accountingPeriodId = this.addRoundingForm.value.accountingPeriodId;
    const profitLossAccountId = this.addRoundingForm.value.profitLossAccountId;
    const balanceSheetAccountId = this.addRoundingForm.value.balanceSheetAccountId;
    const fundId = this.addRoundingForm.value.fundId;

    const accountingPeriod = this.findAccountingPeriodName(accountingPeriodId);
    const profitLossAccountName = this.findAccountName(
      this.profitLossAccounts,
      profitLossAccountId
    );
    const balanceSheetAccountName = this.findAccountName(
      this.balanceSheetAccounts,
      balanceSheetAccountId
    );
    const fund = this.findFundByIdOrName(fundId, null);

    const payload = {
      accountingPeriodId: accountingPeriodId,
      accountingPeriodName: accountingPeriod,
      profitLossRoundingAccountId: profitLossAccountId,
      profitLossRoundingAccountName: profitLossAccountName,
      balanceSheetRoundingAccountId: balanceSheetAccountId,
      balanceSheetRoundingAccountName: balanceSheetAccountName,
      fundRoundingAccountId: fund?.id || fundId || '',
      fundRoundingAccountName: fund?.name || '',
    };

    const action$ = this.isEditMode
      ? this.store.dispatch(new UpdateAutoRoundingSetting(this.data.id!, payload))
      : this.store.dispatch(new SaveAutoRoundingSetting(payload));

    action$.subscribe((res) => {
      const apiResult = res.accountProduction?.autoRoundingSetting;
      const isSuccess = apiResult?.isSuccess;
      const statusMessage =
        apiResult?.statusMessage ||
        (isSuccess ? 'Record saved successfully' : 'Unable to save record');

      if (isSuccess) {
        this.notifier.success(NotificationHeader.success, statusMessage);
        this.dialogRef.close(true);
      } else {
        this.notifier.error(NotificationHeader.error, statusMessage);
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  compareByUuid = (a: any, b: any): boolean => {
    if (a == null || b == null) return false;
    return a.toString().toLowerCase() === b.toString().toLowerCase();
  };

  private loadAccountingPeriods(): void {
    const periods = this.globalComponent.getFinancialPeriod();
    this.accountingPeriods = [...(periods || [])].sort((left, right) => {
      const leftStartDate = this.getAccountingPeriodTime(left?.fromDate);
      const rightStartDate = this.getAccountingPeriodTime(right?.fromDate);

      if (leftStartDate !== rightStartDate) {
        return rightStartDate - leftStartDate;
      }

      const leftEndDate = this.getAccountingPeriodTime(left?.toDate);
      const rightEndDate = this.getAccountingPeriodTime(right?.toDate);

      if (leftEndDate !== rightEndDate) {
        return rightEndDate - leftEndDate;
      }

      return (right?.name || '').localeCompare(left?.name || '');
    });
  }

  private getAccountingPeriodTime(date: string | undefined): number {
    const time = date ? new Date(date).getTime() : NaN;
    return Number.isNaN(time) ? this.accountingPeriodDateFallback : time;
  }

  private findAccountingPeriodName(periodId: any): string {
    const period = this.accountingPeriods.find(
      (p) => p.id?.toString().toLowerCase() === periodId?.toString().toLowerCase()
    );
    return period?.name || '';
  }

  private findAccountName(groups: any[], accountId: any): string {
    for (const group of groups) {
      const match = group.listModels?.find((x: any) => x.id === accountId);
      if (match) return match.name;
    }
    return '';
  }

  private findFundByIdOrName(fundId: any, fundName: string | null): SideListModel | undefined {
    const idMatch = this.fundList.find(
      (x) => x.id?.toString().toLowerCase() === fundId?.toString().toLowerCase()
    );
    if (idMatch) return idMatch;

    if (!fundName) return undefined;

    const normalize = (value: string) =>
      value
        .toLowerCase()
        .replace(/\s*\([a-z]\)\s*$/i, '')
        .replace(/\s+/g, ' ')
        .trim();

    const expected = normalize(fundName);
    return this.fundList.find((x) => normalize(x.name || '') === expected);
  }
}
